import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useIntl } from 'gatsby-plugin-intl'
import { breakpoint } from 'src/theme/grid'
import { LOCATION_TYPES } from 'src/ducks/consts'
import DictionariesService from 'src/services/DictionariesService'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useDispatch } from 'react-redux'
import { setSnackbarValuesRoutine } from 'src/ducks/actions'

const PlaceSuggestion = ({ onClose, placeName, locationType }) => {
  const intl = useIntl()
  const [suggestion, setSuggestion] = useState(placeName || '')
  const [selectedType, setSelectedType] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()

  const isCityType = Array.isArray(locationType)
    ? locationType.includes(LOCATION_TYPES.CITY[0])
    : locationType === LOCATION_TYPES.CITY[0]

  const terminalTypeLabels = {
    sea_terminal: intl.formatMessage({ id: 'offersFilters.sea_terminal' }),
    land_terminal: intl.formatMessage({ id: 'offersFilters.land_terminal' }),
    intermodal_terminal: intl.formatMessage({
      id: 'offersFilters.intermodal_terminal'
    }),
    railway_terminal: intl.formatMessage({
      id: 'offersFilters.railway_terminal'
    })
  }

  useEffect(() => {
    setSuggestion(placeName || '')

    if (isCityType) {
      setSelectedType('city')
    }
  }, [placeName, isCityType])

  const validateForm = () => {
    const newErrors = {}

    if (!suggestion.trim()) {
      newErrors.suggestion = intl.formatMessage({
        id: 'validation.required',
        defaultMessage: 'Pole wymagane'
      })
    }

    if (!postalCode.trim()) {
      newErrors.postalCode = intl.formatMessage({
        id: 'validation.required',
        defaultMessage: 'Pole wymagane'
      })
    }

    if (!selectedType && !locationType) {
      newErrors.selectedType = intl.formatMessage({
        id: 'validation.required',
        defaultMessage: 'Pole wymagane'
      })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    const suggestionData = {
      name: suggestion,
      type:
        selectedType ||
        (Array.isArray(locationType) ? locationType[0] : locationType),
      postalCode: postalCode,
      description: description
    }

    try {
      await DictionariesService.requestNewLocation(suggestionData)
      dispatch(
        setSnackbarValuesRoutine({
          message: intl.formatMessage({
            id: 'common.addPlaceSuccess',
            defaultMessage: 'Formularz został pomyślnie przesłany, dziękujemy!'
          }),
          type: 'success',
          title: ''
        })
      )

      onClose()
    } catch (error) {
      console.error('Wystąpił błąd (create suggestion): ', error)
      setIsLoading(false)

      dispatch(
        setSnackbarValuesRoutine({
          message: intl.formatMessage({
            id: 'common.addPlaceFailure',
            defaultMessage: 'Nastąpił błąd podczas przesyłania formularza'
          }),
          type: 'alert',
          title: ''
        })
      )
    }

    onClose()
  }

  const handleCancel = () => {
    onClose()
  }

  // Determine available location types based on the locationType prop
  const getLocationTypeOptions = () => {
    if (isCityType) {
      return [
        { value: 'city', label: intl.formatMessage({ id: 'common.city' }) }
      ]
    } else {
      return LOCATION_TYPES.TERMINAL.map(type => ({
        value: type,
        label: terminalTypeLabels[type] || type
      }))
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <h3>
            {intl.formatMessage({
              id: 'common.addPlace',
              defaultMessage: 'Dodaj miejsce'
            })}
          </h3>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          <p>{intl.formatMessage({ id: 'offersFilters.addingPlaceText' })}</p>

          <form onSubmit={handleSubmit}>
            <FormField>
              <label htmlFor='place-suggestion'>
                {intl.formatMessage({ id: 'offersFilters.placeName' })}:
                <Required>*</Required>
              </label>
              <input
                id='place-suggestion'
                type='text'
                value={suggestion}
                onChange={e => setSuggestion(e.target.value)}
                placeholder={intl.formatMessage({
                  id: 'offersFilters.placeName'
                })}
                required
              />
              {errors.suggestion && (
                <ErrorMessage>{errors.suggestion}</ErrorMessage>
              )}
            </FormField>
            <FormField>
              <label htmlFor='postal-code'>
                {intl.formatMessage({ id: 'common.postcode' })}:
                <Required>*</Required>
              </label>
              <input
                id='postal-code'
                type='text'
                value={postalCode}
                onChange={e => setPostalCode(e.target.value)}
                placeholder={intl.formatMessage({ id: 'common.postcode' })}
                required
              />
              {errors.postalCode && (
                <ErrorMessage>{errors.postalCode}</ErrorMessage>
              )}
            </FormField>
            <FormField>
              <label htmlFor='place-description'>
                {intl.formatMessage({ id: 'common.description' })}:
              </label>
              <input
                id='place-description'
                type='text'
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={intl.formatMessage({ id: 'common.description' })}
              />
            </FormField>
            <FormField>
              <label htmlFor='place-type'>
                {intl.formatMessage({ id: 'offersFilters.placeType' })}:
                <Required>*</Required>
              </label>
              {isCityType ? (
                // Dla miast wyświetlamy tylko disabled select z predefiniowaną wartością
                <Select id='place-type' value='city' disabled required>
                  <option value='city'>
                    {intl.formatMessage({ id: 'common.city' })}
                  </option>
                </Select>
              ) : (
                // Dla terminali wyświetlamy select z możliwością wyboru
                <Select
                  id='place-type'
                  value={selectedType}
                  onChange={e => setSelectedType(e.target.value)}
                  required
                >
                  <option value='' disabled>
                    {intl.formatMessage({
                      id: 'offersFilters.choosePlaceType'
                    })}
                  </option>
                  {getLocationTypeOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              )}
              {errors.selectedType && (
                <ErrorMessage>{errors.selectedType}</ErrorMessage>
              )}
            </FormField>

            <ButtonGroup>
              <CancelButton type='button' onClick={handleCancel}>
                {intl.formatMessage({
                  id: 'common.cancel',
                  defaultMessage: 'Anuluj'
                })}
              </CancelButton>
              <SubmitButton type='submit'>
                {isLoading ? (
                  <Loader />
                ) : (
                  intl.formatMessage({
                    id: 'common.addSuggestion',
                    defaultMessage: 'Dodaj sugestię'
                  })
                )}
              </SubmitButton>
            </ButtonGroup>
          </form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  )
}

export default PlaceSuggestion

// Style komponentu pozostają bez zmian
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background-color: white;
  border-radius: 3px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);

  ${breakpoint.s`
    width: 80%;
  `}
`

const ModalHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey.hex()};

  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }
`

const ModalBody = styled.div`
  padding: 1.5rem;

  p {
    margin-top: 0;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.colors.black.alpha(0.8).hex()};
  }
`

const FormField = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    font-size: 0.875rem;
  }

  input {
    width: 95%;
    padding: 0.75rem;
    font-size: 1rem;
    border: 1px solid ${({ theme }) => theme.colors.lightGrey.hex()};
    border-radius: 3px;

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.apple.hex()};
    }
  }
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 140px;
  min-height: 35px;
  vertical-align: middle;
`

const CancelButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.wildSand.hex()};
  color: ${({ theme }) => theme.colors.black.hex()};

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightGrey.hex()};
  }
`

const SubmitButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.apple.hex()};
  color: white;
  position: relative;
  transition: background-color 0.2s;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:hover {
    background-color: ${({ theme, disabled }) =>
      disabled ? theme.colors.apple.hex() : theme.colors.deYork.hex()};
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.colors.black.alpha(0.5).hex()};

  &:hover {
    color: ${({ theme }) => theme.colors.black.hex()};
  }
`

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey.hex()};
  border-radius: 3px;
  background-color: ${({ theme }) => theme.colors.white.hex()};
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23242d3f' width='18px' height='18px'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.apple.hex()};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.wildSand.hex()};
    cursor: not-allowed;
  }
`

const Required = styled.span`
  color: red;
  margin-left: 2px;
`

const ErrorMessage = styled.div`
  color: red;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`

const Loader = styled(CircularProgress)`
  width: 25px !important;
  height: 25px !important;
  color: ${({ theme }) => theme.colors.white.hex()} !important;
  transform-origin: center center;
`
